import React, { useEffect, useRef, useState } from "react";
import { Activity, Radio } from "lucide-react";
import { liveCount, makeEvent, PER_MINUTE } from "../lib/liveFeed";
import { useI18n } from "../i18n";

export const LiveActivity = ({ realCount = 0 }) => {
  const { t, lang } = useI18n();
  const [count, setCount] = useState(() => liveCount(realCount));
  const [events, setEvents] = useState(() => Array.from({ length: 5 }, makeEvent).reverse());
  const timer = useRef(null);

  useEffect(() => {
    const tick = setInterval(() => setCount(liveCount(realCount)), 500);
    const schedule = () => {
      timer.current = setTimeout(() => {
        setEvents((prev) => [makeEvent(), ...prev].slice(0, 6));
        schedule();
      }, 2400 + Math.random() * 2600);
    };
    schedule();
    return () => { clearInterval(tick); clearTimeout(timer.current); };
  }, [realCount]);

  const fmt = (n) => n.toLocaleString(lang === "fr" ? "fr-FR" : "en-US");

  return (
    <section className="border-y border-white/5 bg-[#08080a]" data-testid="live-activity">
      <div className="mx-auto grid max-w-6xl gap-10 px-5 py-16 lg:grid-cols-[1fr_1.1fr] lg:items-center">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-300">
            <span className="relative flex h-1.5 w-1.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
            </span>
            {t("live.badge")}
          </span>

          <p data-testid="live-counter" className="mt-6 font-mono2 text-4xl font-medium tabular-nums tracking-tight text-white sm:text-5xl">
            {fmt(count)}
          </p>
          <p className="mt-3 text-sm text-slate-400">{t("live.label")}</p>

          <div className="mt-7 flex flex-wrap gap-x-8 gap-y-3 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1.5">
              <Activity className="h-3.5 w-3.5 text-sky-400" strokeWidth={1.75} />
              <span className="font-mono2 text-slate-300">≈ {fmt(PER_MINUTE)}</span> {t("live.perMinute")}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Radio className="h-3.5 w-3.5 text-sky-400" strokeWidth={1.75} />
              {t("live.local")}
            </span>
          </div>
        </div>

        <div className="rounded-2xl border border-white/[0.07] bg-[#0b0b0d]">
          <div className="flex items-center gap-2 border-b border-white/[0.07] px-4 py-2.5">
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{t("live.feed")}</span>
            <span className="ml-auto font-mono2 text-[10px] text-emerald-400">● live</span>
          </div>
          <ul data-testid="live-feed" className="divide-y divide-white/[0.05]">
            {events.map((e) => (
              <li key={e.id} className="rise flex items-center gap-3 px-4 py-2.5">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-sky-400" />
                <span className="truncate font-mono2 text-[12px] text-slate-300">{e.file}</span>
                <span className="shrink-0 rounded border border-white/10 bg-white/5 px-1.5 py-px font-mono2 text-[10px] uppercase text-slate-400">
                  {e.mark}
                </span>
                <span className="ml-auto shrink-0 font-mono2 text-[10px] text-slate-600">
                  {e.marks} {t("live.marks")} · {e.region}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </div>
      <p className="mx-auto max-w-6xl px-5 pb-8 text-[11px] text-slate-600">{t("live.note")}</p>
    </section>
  );
};
